import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Card from "../shared/Card";
import axios from "axios";
import { getCurrentAdmin } from "../../store/thunks/authThunks";

const API_URL = import.meta.env.VITE_API_URL;

const Dashboard = () => {
  const { admin } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [stats, setStats] = useState({
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalPrice: 0,
    totalProjects: 0,
    totalUsers: 0,
    usersByPlan: {
      Freemium: 0,
      Pro: 0,
      Enterprise: 0,
      Team: 0,
      "No Plan": 0,
    },
    htmlStats: {
      count: 0,
      inputTokens: 0,
      outputTokens: 0,
      totalPrice: 0,
    },
    fullstackStats: {
      count: 0,
      inputTokens: 0,
      outputTokens: 0,
      totalPrice: 0,
    },
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Function to calculate cost for Fullstack projects
  const calculateFullstackCost = (inputTokens, outputTokens) => {
    const input = inputTokens || 0;
    const output = outputTokens || 0;
    return (input * 0.000003) + (output * 0.000015);
  };

  useEffect(() => {
    // Fetch current admin data when dashboard loads
    dispatch(getCurrentAdmin());
    fetchStats();
  }, [dispatch]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all projects
      const projectsResponse = await axios.get(`${API_URL}/api/projects`);
      const projects = projectsResponse.data;

      // Fetch all users
      const usersResponse = await axios.get(`${API_URL}/api/users`);
      const users = usersResponse.data;

      // Fetch subscriptions and plans
      const token = localStorage.getItem('token'); // or get from redux if you use it
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const [subscriptionsResponse, plansResponse] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions`, config),
        axios.get(`${API_URL}/api/plans`, config),
      ]);
      const subscriptions = subscriptionsResponse.data;
      const plans = plansResponse.data;

      // Map plans and subscriptions
      const plansMap = plans.reduce((acc, plan) => {
        acc[plan._id] = plan;
        return acc;
      }, {});
      const subscriptionsByUser = subscriptions.reduce((acc, sub) => {
        if (sub.status === 'active') {
          acc[sub.user] = sub;
        }
        return acc;
      }, {});

      // Merge plan info into users
      const usersWithPlans = users.map(user => {
        const userSubscription = subscriptionsByUser[user._id];
        const userPlan = userSubscription ? plansMap[userSubscription.plan] : null;
        return {
          ...user,
          subscriptionPlan: userPlan ? userPlan.name : 'Freemium',
        };
      });

      // Count users by plan
      const usersByPlan = usersWithPlans.reduce((acc, user) => {
        const plan = user.subscriptionPlan || "No Plan";
        acc[plan] = (acc[plan] || 0) + 1;
        return acc;
      }, {
        Freemium: 0,
        Pro: 0,
        Enterprise: 0,
        Team: 0,
        Business: 0,
        Exclusive: 0,
        "No Plan": 0,
      });

      // Calculate project statistics
      const projectStats = projects.reduce(
        (acc, project) => {
          // Update total stats
          acc.totalProjects++;
          acc.totalInputTokens += project.tokens?.input_tokens || 0;
          acc.totalOutputTokens += project.tokens?.output_tokens || 0;
          
          // Calculate project cost including Fullstack token cost
          let projectCost = parseFloat(project.price) || 0;
          if (project.type === "Fullstack") {
            const fullstackTokenCost = calculateFullstackCost(
              project.tokens?.input_tokens,
              project.tokens?.output_tokens
            );
            projectCost += fullstackTokenCost;
          }
          acc.totalPrice += projectCost;

          // Update HTML/CSS specific stats
          if (project.type === "HTML/CSS") {
            acc.htmlStats.count++;
            acc.htmlStats.inputTokens += project.tokens?.input_tokens || 0;
            acc.htmlStats.outputTokens += project.tokens?.output_tokens || 0;
            acc.htmlStats.totalPrice += parseFloat(project.price) || 0;
          }
          
          // Update Fullstack specific stats
          if (project.type === "Fullstack") {
            acc.fullstackStats.count++;
            acc.fullstackStats.inputTokens += project.tokens?.input_tokens || 0;
            acc.fullstackStats.outputTokens += project.tokens?.output_tokens || 0;
            
            // For Fullstack stats, include both base price and calculated token cost
            const basePrice = parseFloat(project.price) || 0;
            const tokenCost = calculateFullstackCost(
              project.tokens?.input_tokens,
              project.tokens?.output_tokens
            );
            acc.fullstackStats.totalPrice += basePrice + tokenCost;
          }

          return acc;
        },
        {
          totalInputTokens: 0,
          totalOutputTokens: 0,
          totalPrice: 0,
          totalProjects: 0,
          htmlStats: {
            count: 0,
            inputTokens: 0,
            outputTokens: 0,
            totalPrice: 0,
          },
          fullstackStats: {
            count: 0,
            inputTokens: 0,
            outputTokens: 0,
            totalPrice: 0,
          },
        }
      );

      setStats({
        ...projectStats,
        totalUsers: usersWithPlans.length,
        usersByPlan,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      setError("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  if (error) {
    return <div className="text-center py-4 text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {admin?.role === "admin" && (
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Total Projects
                  </h3>
                  <p className="text-3xl font-bold text-orange-600">
                    {formatNumber(stats.totalProjects)}
                  </p>
                </div>
              </Card>
            )}
            {admin?.role === "admin" && (
              <Card>
                <div 
                  className="p-6 relative" 
                  onMouseEnter={() => setShowTooltip(true)} 
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Total Users
                  </h3>
                  <p className="text-3xl font-bold text-red-600">
                    {formatNumber(stats.totalUsers)}
                  </p>
                  
                  {showTooltip && (
                    <div className="absolute z-10 bg-white rounded-lg p-3 w-48 -top-3 left-1/3 transform -translate-x-1/2 mt-0">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Users by Plan</h4>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Freemium:</span>
                          <span className="text-sm font-medium">{formatNumber(stats.usersByPlan.Freemium)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Pro:</span>
                          <span className="text-sm font-medium">{formatNumber(stats.usersByPlan.Pro)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Enterprise:</span>
                          <span className="text-sm font-medium">{formatNumber(stats.usersByPlan.Enterprise)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Team:</span>
                          <span className="text-sm font-medium">{formatNumber(stats.usersByPlan.Team)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
            {admin?.role === "admin" && stats.htmlStats.count > 0 && (
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    HTML/CSS Projects
                  </h3>
                  <p className="text-3xl font-bold text-indigo-600">
                    {formatNumber(stats.htmlStats.count)}
                  </p>
                </div>
              </Card>
            )}
            {admin?.role === "admin" && (
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Fullstack Projects
                  </h3>
                  <p className="text-3xl font-bold text-cyan-600">
                    {formatNumber(stats.fullstackStats.count)}
                  </p>
                </div>
              </Card>
            )}
          </div>

          {admin?.role === "admin" && stats.htmlStats.count > 0 && (
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              HTML/CSS Projects Statistics
            </h2>
          )}
          {admin?.role === "admin" && stats.htmlStats.count > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Input Tokens
                  </h3>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatNumber(stats.htmlStats.inputTokens)}
                  </p>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Output Tokens
                  </h3>
                  <p className="text-3xl font-bold text-green-600">
                    {formatNumber(stats.htmlStats.outputTokens)}
                  </p>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Total Cost
                  </h3>
                  <p className="text-3xl font-bold text-purple-600">
                    {formatPrice(stats.htmlStats.totalPrice)}
                  </p>
                </div>
              </Card>
            </div>
          )}

          {/* Fullstack Projects Statistics */}
          {admin?.role === "admin" && (
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              Fullstack Projects Statistics
            </h2>
          )}
          {admin?.role === "admin" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Input Tokens
                  </h3>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatNumber(stats.fullstackStats.inputTokens)}
                  </p>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Output Tokens
                  </h3>
                  <p className="text-3xl font-bold text-green-600">
                    {formatNumber(stats.fullstackStats.outputTokens)}
                  </p>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Total Cost
                  </h3>
                  <p className="text-3xl font-bold text-purple-600">
                    {formatPrice(stats.fullstackStats.totalPrice)}
                  </p>
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;